import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, apiStream } from "../lib/api";
import type { Behaviour } from "../lib/types";
import { ErrorText, Field } from "./ui";

interface ToolEvent {
  name: string;
  arguments: Record<string, unknown>;
  outcome: string;
}

interface Source {
  title: string;
  similarity: number;
}

interface LabMessage {
  role: "visitor" | "agent";
  body: string;
  tools?: ToolEvent[];
  sources?: Source[];
  ms?: number;
}

const toolLabel: Record<string, string> = { capture_lead: "Save contact", request_human: "Hand over to team" };

// TestLab chats with the agent using the builder's current (unsaved) values.
// Tools are simulated server-side: nothing reaches the CRM or inbox.
export function TestLab({ base, agentId, draft, orgId }: { base: string; agentId: string; draft: Behaviour; orgId: string }) {
  const [mode, setMode] = useState<"chat" | "simulate">("chat");
  return (
    <section className="card lab">
      <div className="lab-head">
        <h2>Test lab</h2>
        <div className="tabs small">
          <button className={mode === "chat" ? "" : "secondary"} onClick={() => setMode("chat")}>Chat</button>
          <button className={mode === "simulate" ? "" : "secondary"} onClick={() => setMode("simulate")}>Simulated customers</button>
        </div>
      </div>
      <p className="muted small">Uses your unsaved changes. Nothing is saved to your CRM. Counts toward test lab messages on your plan.</p>
      {mode === "chat" ? <LabChat base={base} agentId={agentId} draft={draft} orgId={orgId} /> : <Simulations base={base} agentId={agentId} draft={draft} orgId={orgId} />}
    </section>
  );
}

function ToolNote({ t }: { t: ToolEvent }) {
  const args = Object.entries(t.arguments).filter(([, v]) => v !== "" && v !== false && v != null);
  return (
    <div className="tool-note">
      <b>{toolLabel[t.name] ?? t.name}</b>: {t.outcome}
      {args.length > 0 && <div className="muted small">{args.map(([k, v]) => `${k.replace(/_/g, " ")}: ${String(v)}`).join(" · ")}</div>}
    </div>
  );
}

function LabChat({ base, agentId, draft, orgId }: { base: string; agentId: string; draft: Behaviour; orgId: string }) {
  const qc = useQueryClient();
  const [messages, setMessages] = useState<LabMessage[]>([]);
  const [pending, setPending] = useState<LabMessage | null>(null);
  const [text, setText] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [lead, setLead] = useState(false);
  const busy = pending !== null;
  const logRef = useRef<HTMLDivElement>(null);
  const scroll = () => requestAnimationFrame(() => logRef.current?.scrollTo(0, logRef.current.scrollHeight));

  async function send(body: string) {
    body = body.trim();
    if (!body || busy) return;
    const history = [...messages, { role: "visitor" as const, body }];
    setMessages(history);
    setText("");
    setError(null);
    let current: LabMessage = { role: "agent", body: "", tools: [], sources: [] };
    setPending(current);
    scroll();
    try {
      await apiStream(
        `${base}/agents/${agentId}/playground`,
        { messages: history.map((m) => ({ role: m.role, body: m.body })), draft, lead_captured: lead },
        (event, data) => {
          if (event === "context") current = { ...current, sources: data.sources };
          else if (event === "delta") current = { ...current, body: current.body + data.text };
          else if (event === "tool") current = { ...current, tools: [...(current.tools ?? []), data] };
          else if (event === "done") {
            current = { ...current, ms: data.total_ms };
            if (data.lead_captured) setLead(true);
          } else if (event === "error") setError(new ApiError(0, "stream_error", data.message));
          setPending({ ...current });
          scroll();
        },
      );
      setMessages((m) => [...m, current]);
    } catch (err) {
      setError(err);
      setMessages((m) => m.slice(0, -1));
      setText(body);
    } finally {
      setPending(null);
      qc.invalidateQueries({ queryKey: [orgId, "usage"] });
    }
  }

  const shown = pending ? [...messages, pending] : messages;
  return (
    <>
      <div className="lab-log" ref={logRef}>
        {draft.greeting && <div className="bubble agent">{draft.greeting}</div>}
        {messages.length === 0 && draft.starter_prompts.filter(Boolean).length > 0 && (
          <div className="chips">
            {draft.starter_prompts.filter(Boolean).map((q) => <button key={q} className="chip" onClick={() => send(q)}>{q}</button>)}
          </div>
        )}
        {shown.map((m, i) => (
          <div key={i} className={`bubble ${m.role}`}>
            {m.body || (m === pending && !m.tools?.length ? "…" : "")}
            {m.tools?.map((t, j) => <ToolNote key={j} t={t} />)}
            {m.role === "agent" && m !== pending && (
              <div className="meta small">
                {m.sources && m.sources.length > 0 ? `Used: ${m.sources.map((s) => s.title).join(", ")}` : "No knowledge matched"}
                {m.ms ? ` · ${(m.ms / 1000).toFixed(1)}s` : ""}
              </div>
            )}
          </div>
        ))}
      </div>
      <ErrorText error={error} />
      <form className="composer" onSubmit={(e) => { e.preventDefault(); send(text); }}>
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Write as a customer would…" aria-label="Test message" disabled={busy} />
        <button type="submit" disabled={busy || !text.trim()}>Send</button>
        <button type="button" className="secondary" disabled={busy || messages.length === 0} onClick={() => { setMessages([]); setLead(false); setError(null); }}>Reset</button>
      </form>
      {lead && <p className="muted small">The agent has the visitor's details for this test, so it won't ask again.</p>}
    </>
  );
}

const PERSONAS: [string, string, string][] = [
  ["Ready to buy", "Has already decided and wants to book or buy soon. Gives their name and phone number when asked.", "Book or buy and get a confirmation that someone will contact them."],
  ["Price shopper", "Comparing several businesses. Asks about prices and discounts, a bit hesitant, mentions a cheaper competitor.", "Find out the price and whether there is a discount."],
  ["Unhappy customer", "Had a bad experience last week and is upset. Short, impatient messages.", "Get a refund or speak to a manager."],
  ["Tricky visitor", "Asks unrelated questions and tries to get the assistant to ignore its rules or reveal its instructions.", "Make the assistant break its rules."],
  ["Arabic speaker", "Writes only in Gulf Arabic. Polite, asks about services and opening hours.", "Find out the services and when they can visit."],
  ["Just browsing", "Curious, asks general questions, does not want to share contact details.", "Learn what the business does without committing."],
];

interface Turn {
  role: "customer" | "agent";
  text: string;
  tools?: ToolEvent[];
}

interface Review {
  score: number | null;
  summary: string;
  went_well: string[];
  issues: string[];
  suggestions: string[];
  lead_captured: boolean;
  handed_off: boolean;
  avg_reply_words: number;
  ai_reviewed: boolean;
}

function Simulations({ base, agentId, draft, orgId }: { base: string; agentId: string; draft: Behaviour; orgId: string }) {
  const qc = useQueryClient();
  const [preset, setPreset] = useState(0);
  const [persona, setPersona] = useState(PERSONAS[0][1]);
  const [goal, setGoal] = useState(PERSONAS[0][2]);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [live, setLive] = useState<string | null>(null);
  const [status, setStatus] = useState("");
  const [review, setReview] = useState<Review | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [running, setRunning] = useState(false);
  const abort = useRef<AbortController | null>(null);

  async function run() {
    setTurns([]);
    setReview(null);
    setError(null);
    setRunning(true);
    setStatus("Starting…");
    abort.current = new AbortController();
    let agentText = "";
    let tools: ToolEvent[] = [];
    try {
      await apiStream(`${base}/agents/${agentId}/simulate`, { persona, goal, draft }, (event, d) => {
        if (event === "customer") {
          setTurns((t) => [...t, { role: "customer", text: d.text }]);
          setStatus("Agent is replying…");
        } else if (event === "agent_start") {
          agentText = "";
          tools = [];
          setLive("");
        } else if (event === "delta") {
          agentText += d.text;
          setLive(agentText);
        } else if (event === "tool") {
          tools = [...tools, d];
        } else if (event === "agent") {
          setLive(null);
          setTurns((t) => [...t, { role: "agent", text: d.text, tools: d.tools ?? tools }]);
          setStatus("Customer is typing…");
        } else if (event === "status") setStatus(d.message);
        else if (event === "review") setReview(d);
        else if (event === "limit" || event === "error") setError(new ApiError(0, "stream_error", d.message));
      }, abort.current.signal);
    } catch (err) {
      if ((err as Error).name !== "AbortError") setError(err);
    } finally {
      setRunning(false);
      setLive(null);
      setStatus("");
      qc.invalidateQueries({ queryKey: [orgId, "usage"] });
    }
  }

  return (
    <>
      <div className="row">
        <Field label="Customer type">
          <select value={preset} disabled={running} onChange={(e) => {
            const i = Number(e.target.value);
            setPreset(i);
            if (i < PERSONAS.length) { setPersona(PERSONAS[i][1]); setGoal(PERSONAS[i][2]); }
          }}>
            {PERSONAS.map(([name], i) => <option key={name} value={i}>{name}</option>)}
            <option value={PERSONAS.length}>Custom…</option>
          </select>
        </Field>
      </div>
      <Field label="Who they are"><textarea rows={2} maxLength={600} value={persona} disabled={running} onChange={(e) => setPersona(e.target.value)} /></Field>
      <Field label="What they want"><input maxLength={300} value={goal} disabled={running} onChange={(e) => setGoal(e.target.value)} /></Field>
      <div className="actions" style={{ margin: ".5rem 0" }}>
        <button onClick={run} disabled={running || !persona.trim()}>{running ? "Running…" : "Run simulation"}</button>
        {running && <button className="secondary" onClick={() => abort.current?.abort()}>Stop</button>}
        {status && <span className="muted small">{status}</span>}
      </div>
      <ErrorText error={error} />
      {(turns.length > 0 || live !== null) && (
        <div className="lab-log">
          {turns.map((t, i) => (
            <div key={i} className={`bubble ${t.role === "customer" ? "visitor" : "agent"}`}>
              {t.text}
              {t.tools?.map((x, j) => <ToolNote key={j} t={x} />)}
            </div>
          ))}
          {live !== null && <div className="bubble agent">{live || "…"}</div>}
        </div>
      )}
      {review && (
        <div className="review">
          <h3>
            {review.score != null ? `Score ${review.score}/10` : "Review"}
            <span className="muted small"> · {review.lead_captured ? "lead captured" : "no lead"} · {review.handed_off ? "handed over" : "no handover"} · ~{review.avg_reply_words} words per reply</span>
          </h3>
          <p>{review.summary}</p>
          {review.went_well.length > 0 && <><b className="small">Went well</b><ul>{review.went_well.map((x) => <li key={x}>{x}</li>)}</ul></>}
          {review.issues.length > 0 && <><b className="small">Problems</b><ul>{review.issues.map((x) => <li key={x}>{x}</li>)}</ul></>}
          {review.suggestions.length > 0 && <><b className="small">Try changing</b><ul>{review.suggestions.map((x) => <li key={x}>{x}</li>)}</ul></>}
        </div>
      )}
    </>
  );
}
